import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { reconciliation, designMappings } from '../services/api';
import { UploadCloud, AlertTriangle, RefreshCw } from 'lucide-react';

interface DiffRow {
  design: string;
  excel_qty: number;
  app_qty: number;
  diff: number;
  mapped: boolean;
  mapped_to: string | null;
  reason: string | null;
}

interface CheckResult {
  date_range: { from: string | null; to: string | null };
  skipped_rows: number;
  totals: { excel_units: number; app_units: number; app_mapped_units: number };
  rows: DiffRow[];
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl)', padding: 24,
};

const BlankMaterialCheck: React.FC = () => {
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CheckResult | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [blanks, setBlanks] = useState<{ article: string; color: string; label: string }[]>([]);
  const [assignments, setAssignments] = useState<Record<string, { article: string; color: string }>>({});
  const [savingMappings, setSavingMappings] = useState(false);
  const [mapMsg, setMapMsg] = useState('');

  const handleFile = async (file: File) => {
    setUploadedFile(file);
    setLoading(true);
    setError('');
    try {
      const res = await reconciliation.blankMaterials(file);
      setResult(res.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to parse file');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (result && result.rows.some(r => !r.mapped) && blanks.length === 0) {
      designMappings.getInventoryBlanks().then(r => setBlanks(r.data.all || [])).catch(() => {});
    }
  }, [result, blanks.length]);

  const reset = () => { setResult(null); setError(''); };

  const setAssignment = (title: string, article: string, color: string) =>
    setAssignments(prev => ({ ...prev, [title]: { article, color } }));
  const removeAssignment = (title: string) =>
    setAssignments(prev => { const n = { ...prev }; delete n[title]; return n; });

  const saveMappings = async () => {
    const entries = Object.entries(assignments);
    if (!entries.length) return;
    setSavingMappings(true);
    setMapMsg('');
    try {
      const data = entries.map(([title, { article, color }]) => ({ shopify_title: title, blank_article: article, blank_color: color }));
      const r = await designMappings.createBulk(data);
      setMapMsg(`${r.data.created} created, ${r.data.updated} updated, ${r.data.backfilled_units} existing units re-linked`);
      setAssignments({});
      if (uploadedFile) await handleFile(uploadedFile);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to save mappings');
    }
    setSavingMappings(false);
  };

  return (
    <Layout title="Blank Material Check">
      {!result && (
        <div style={{ ...cardStyle, maxWidth: 520 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
            Upload Shopify Export
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
            Upload a Shopify orders export already filtered to paid orders for a month.
            This compares design-by-design totals against the app's own data — read-only,
            nothing here is saved or changed.
          </div>
          <label
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={e => {
              e.preventDefault();
              setDragActive(false);
              const f = e.dataTransfer.files[0];
              if (f) handleFile(f);
            }}
            style={{
              display: 'block', border: `2px dashed ${dragActive ? '#2dd4a0' : 'var(--border)'}`,
              borderRadius: 10, padding: '40px 24px', textAlign: 'center', cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
          >
            <input
              type="file" accept=".xlsx" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <UploadCloud size={28} color="var(--text-muted)" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {loading ? 'Analysing…' : 'Drop the .xlsx file here, or click to browse'}
            </div>
          </label>
          {error && (
            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--accent-red)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}
        </div>
      )}

      {result && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {result.date_range.from} → {result.date_range.to}
              {result.skipped_rows > 0 && ` · ${result.skipped_rows} row(s) skipped (unparseable date)`}
            </div>
            <button
              onClick={reset}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-elevated)',
                border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px',
                fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
              }}
            >
              <RefreshCw size={13} /> Upload another file
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Excel Total</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{result.totals.excel_units}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>App Total (all)</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{result.totals.app_units}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>App Total (counted in P&L)</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{result.totals.app_mapped_units}</div>
              {result.totals.app_units > result.totals.app_mapped_units && (
                <div style={{ fontSize: 12, color: 'var(--accent-red)', marginTop: 4 }}>
                  {result.totals.app_units - result.totals.app_mapped_units} units excluded from P&L (missing blank mapping)
                </div>
              )}
            </div>
          </div>

          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-elevated)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Design</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Excel Qty</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>App Qty</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Diff</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map(row => (
                  <tr key={row.design} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', color: 'var(--text-primary)' }}>{row.design}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{row.excel_qty}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{row.app_qty}</td>
                    <td style={{ padding: '12px 16px', color: row.diff !== 0 ? 'var(--accent-red)' : 'var(--text-muted)', fontWeight: row.diff !== 0 ? 700 : 400 }}>
                      {row.diff > 0 ? `+${row.diff}` : row.diff}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {row.mapped ? (
                        <span style={{ color: 'var(--accent-green)', fontSize: 12 }}>Mapped → {row.mapped_to}</span>
                      ) : (
                        <span title={row.reason || ''} style={{ color: 'var(--accent-red)', fontSize: 12 }}>Unmapped</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.rows.some(r => !r.mapped) && (
            <div style={{ ...cardStyle, marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Map Remaining Products
                </div>
                {Object.keys(assignments).length > 0 && (
                  <button
                    onClick={saveMappings}
                    disabled={savingMappings}
                    style={{
                      padding: '8px 18px', background: 'linear-gradient(135deg, var(--accent-green), #059669)',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 700,
                      cursor: savingMappings ? 'default' : 'pointer',
                    }}
                  >
                    {savingMappings ? 'Saving…' : `Save Mappings (${Object.keys(assignments).length})`}
                  </button>
                )}
              </div>
              {mapMsg && (
                <div style={{ fontSize: 12, color: 'var(--accent-green)', marginBottom: 10 }}>{mapMsg}</div>
              )}
              {result.rows.filter(r => !r.mapped).sort((a, b) => b.excel_qty - a.excel_qty).map(row => {
                const a = assignments[row.design];
                return (
                  <div key={row.design} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.design}
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, minWidth: 70, textAlign: 'right' }}>
                      {row.excel_qty} units
                    </span>
                    <select
                      value={a ? `${a.article} — ${a.color}` : ''}
                      onChange={e => {
                        if (!e.target.value) { removeAssignment(row.design); return; }
                        const [article, color] = e.target.value.split(' — ');
                        setAssignment(row.design, article.trim(), color.trim());
                      }}
                      style={{
                        padding: '5px 8px', background: a ? 'var(--accent-green-soft)' : 'var(--bg-input)',
                        border: `1px solid ${a ? 'rgba(45,212,160,0.3)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)',
                        color: a ? 'var(--accent-green)' : 'var(--text-primary)', fontSize: 11.5, outline: 'none',
                        minWidth: 170, fontWeight: a ? 600 : 400,
                      }}
                    >
                      <option value="">Choose blank...</option>
                      {blanks.map(b => (
                        <option key={`${b.article} — ${b.color}`} value={`${b.article} — ${b.color}`}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default BlankMaterialCheck;
